/**
 * Collapsible Section Component
 * Reusable wrapper for sections that can be collapsed/expanded
 * Follows Single Responsibility Principle
 */

import React, { useState, useEffect } from 'react';
import { IconButton } from '@/design-system';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';

export interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  /**
   * Controlled collapsed state. If provided, component becomes controlled.
   * When switching from uncontrolled to controlled, internal state is synced.
   */
  isCollapsed?: boolean;
  /**
   * Callback when collapse button is clicked (for controlled mode)
   */
  onToggle?: (collapsed: boolean) => void;
  showCollapseButton?: boolean;
  headerActions?: React.ReactNode;
  className?: string;
  titleStyle?: React.CSSProperties;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultCollapsed = false,
  isCollapsed: controlledIsCollapsed,
  onToggle,
  showCollapseButton = true,
  headerActions,
  className,
  titleStyle,
}) => {
  const [internalIsCollapsed, setInternalIsCollapsed] =
    useState(defaultCollapsed);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledIsCollapsed !== undefined;
  const isCollapsed = isControlled
    ? controlledIsCollapsed
    : internalIsCollapsed;

  // Sync internal state when switching from uncontrolled to controlled
  useEffect(() => {
    if (isControlled && controlledIsCollapsed !== undefined) {
      setInternalIsCollapsed(controlledIsCollapsed);
    }
  }, [isControlled, controlledIsCollapsed]);

  const handleToggle = () => {
    if (isControlled) {
      // Controlled mode - call parent callback
      onToggle?.(!isCollapsed);
    } else {
      // Uncontrolled mode - update internal state
      setInternalIsCollapsed(!internalIsCollapsed);
    }
  };

  const defaultTitleStyle: React.CSSProperties = {
    fontFamily: fontPrimary,
    fontSize: typography.size.lg,
    fontWeight: typography.weight.semibold,
    color: typographyColors.primary,
    lineHeight: 'normal',
    ...titleStyle,
  };

  return (
    <div className={className}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h3 style={defaultTitleStyle}>{title}</h3>
        <div style={{ display: 'flex', gap: spacing[2], alignItems: 'center' }}>
          {headerActions}
          {showCollapseButton && (
            <IconButton
              icon={
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  style={{
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <path
                    d='M5 7.5L10 12.5L15 7.5'
                    stroke={typographyColors.primary}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              }
              onClick={handleToggle}
              ariaLabel={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
            />
          )}
        </div>
      </div>
      {!isCollapsed && <div style={{ marginTop: spacing[4] }}>{children}</div>}
    </div>
  );
};
